if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        this.closest('product-info')?.querySelector('variant-selects')?.syncCurrentVariantId?.();

        const selectedVariantId = this.getSelectedVariantId();
        if (selectedVariantId) {
          this.variantIdInput.value = selectedVariantId;
        }

        const formData = new FormData(this.form);
        if (selectedVariantId) {
          formData.set('id', selectedVariantId);
        }
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      getSelectedVariantId() {
        const productInfo = this.closest('product-info');
        const variantSelects = productInfo?.querySelector('variant-selects');
        const variantsData = variantSelects?.querySelector('[data-product-variants]');
        if (!variantSelects || !variantsData) return this.variantIdInput.value;

        let variants = [];
        try {
          variants = JSON.parse(variantsData.textContent);
        } catch (e) {
          return this.variantIdInput.value;
        }

        const syncedVariantId = variantSelects.dataset.currentVariantId || productInfo?.dataset.currentVariantId;
        if (syncedVariantId && variants.some((variant) => String(variant.id) === String(syncedVariantId))) {
          return syncedVariantId;
        }

        const optionCount = variants[0]
          ? ['option1', 'option2', 'option3'].filter((key) => variants[0][key] !== null).length
          : 0;
        const selectedValues = [];
        const selectedPills = variantSelects.querySelectorAll('.cvs-pill.is-selected');

        selectedPills.forEach((pill) => {
          const position = parseInt(pill.dataset.optionPosition, 10);
          if (!position) return;
          selectedValues[position - 1] = pill.dataset.optionValue;
        });

        variantSelects.querySelectorAll('fieldset input:checked, select').forEach((input) => {
          if (selectedValues.filter((value) => value !== undefined).length >= optionCount) return;

          if (input.tagName === 'SELECT') {
            const dataIndex = input.dataset.index || '';
            const match = dataIndex.match(/option(\d+)/);
            if (!match) return;
            selectedValues[parseInt(match[1], 10) - 1] = input.value;
            return;
          }

          const nameParts = (input.name || '').split('-');
          const position = parseInt(nameParts[nameParts.length - 1], 10);
          if (!position) return;
          selectedValues[position - 1] = input.value;
        });

        if (selectedValues.filter((value) => value !== undefined).length < optionCount) {
          return this.variantIdInput.value;
        }

        const matchedVariant = variants.find((variant) =>
          selectedValues.slice(0, optionCount).every((value, index) => variant[`option${index + 1}`] === value)
        );

        if (matchedVariant?.id) {
          return matchedVariant.id;
        }

        const currentInputVariantId = this.variantIdInput.value;
        if (currentInputVariantId && variants.some((variant) => String(variant.id) === String(currentInputVariantId))) {
          return currentInputVariantId;
        }

        const urlVariantId = new URLSearchParams(window.location.search).get('variant');
        if (urlVariantId && variants.some((variant) => String(variant.id) === urlVariantId)) {
          return urlVariantId;
        }

        return this.variantIdInput.value;
      }
    }
  );
}
